import React, { useState } from 'react';
import axios from 'axios';
import DuplicateWarning from './DuplicateWarning';
import { useSystemSettings } from '../hooks/useSystemSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateProjectModal = ({ onClose, onProjectCreated }) => {
  const { industries } = useSystemSettings();
  
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    industry: 'energy',
    customer_company: '',
    budget_amount: '',
    budget_currency: 'KZT',
    readiness_phase: 'concept',
    sources: [],
    description: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sourceInput, setSourceInput] = useState('');
  
  // Duplicate detection states
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  // Use dynamic industries from system settings
  const industryOptions = industries.map(industry => ({
    value: industry.id,
    label: industry.title
  }));

  const readinessOptions = [
    { value: 'concept', label: 'Концепт' },
    { value: 'design', label: 'Проектирование' },
    { value: 'construction_start', label: 'Начало строительства' },
    { value: 'active_construction', label: 'Активная стройка' },
    { value: 'approaching_em', label: 'Подходит к ЭМ' },
    { value: 'completion', label: 'Завершение' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleAddSource = () => {
    if (sourceInput.trim()) {
      setFormData({
        ...formData,
        sources: [...formData.sources, sourceInput.trim()]
      });
      setSourceInput('');
    }
  };

  const handleRemoveSource = (index) => {
    setFormData({
      ...formData,
      sources: formData.sources.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsCheckingDuplicates(true);

    try {
      const projectData = {
        ...formData,
        budget_amount: (formData.budget_amount && formData.budget_amount.trim() !== "") ? parseFloat(formData.budget_amount) : null
      };

      // Check for duplicates first
      const duplicateResponse = await axios.post(`${API}/projects/check-duplicates`, projectData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (duplicateResponse.data.has_duplicates) {
        setDuplicates(duplicateResponse.data.duplicates);
        setShowDuplicateWarning(true);
        setIsCheckingDuplicates(false);
        return;
      }

      // If no duplicates, create project directly
      await createProject(false);
      
    } catch (err) {
      console.error('Failed to check duplicates:', err);
      setError(err.response?.data?.detail || 'Ошибка при проверке дублей');
      setIsCheckingDuplicates(false);
    }
  };

  const createProject = async (forceCreate = false) => {
    setLoading(true);
    setError('');

    try {
      const projectData = {
        ...formData,
        budget_amount: (formData.budget_amount && formData.budget_amount.trim() !== "") ? parseFloat(formData.budget_amount) : null
      };

      const response = await axios.post(
        `${API}/projects/create-with-duplicate-check?force_create=${forceCreate}`, 
        projectData,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      if (response.data.action === 'created') {
        onProjectCreated(response.data.project);
      }
      
    } catch (err) {
      console.error('Failed to create project:', err);
      setError(err.response?.data?.detail || 'Не удалось создать проект');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDuplicateCreation = () => {
    setShowDuplicateWarning(false);
    createProject(true);
  };

  const handleCancelDuplicateCreation = () => {
    setShowDuplicateWarning(false);
    setDuplicates([]);
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Создать проект</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="close-modal-button"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-group">
                  <label className="form-label" htmlFor="name">
                    Название проекта *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Название проекта"
                    required
                    data-testid="project-name-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="city">
                    Город *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Город"
                    required
                    data-testid="city-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-group">
                  <label className="form-label" htmlFor="industry">
                    Отрасль *
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    className="form-select"
                    required
                    data-testid="industry-select"
                  >
                    {industryOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="readiness_phase">
                    Фаза готовности
                  </label>
                  <select
                    id="readiness_phase"
                    name="readiness_phase"
                    value={formData.readiness_phase}
                    onChange={handleChange}
                    className="form-select"
                    data-testid="readiness-select"
                  >
                    {readinessOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="customer_company">
                  Заказчик
                </label>
                <input
                  type="text"
                  id="customer_company"
                  name="customer_company"
                  value={formData.customer_company}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Название компании заказчика"
                  data-testid="customer-input"
                />
              </div>

              {/* Budget */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-group">
                  <label className="form-label" htmlFor="budget_amount">
                    Бюджет
                  </label>
                  <input
                    type="number"
                    id="budget_amount"
                    name="budget_amount"
                    value={formData.budget_amount}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="0"
                    min="0"
                    data-testid="budget-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="budget_currency">
                    Валюта
                  </label>
                  <select
                    id="budget_currency"
                    name="budget_currency"
                    value={formData.budget_currency}
                    onChange={handleChange}
                    className="form-select"
                    data-testid="currency-select"
                  >
                    <option value="KZT">KZT</option>
                    <option value="USD">USD</option>
                    <option value="RUB">RUB</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>

              {/* Sources */}
              <div className="form-group">
                <label className="form-label">
                  Источники информации
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="url"
                    value={sourceInput}
                    onChange={(e) => setSourceInput(e.target.value)}
                    className="form-input flex-1"
                    placeholder="https://example.com"
                    data-testid="source-input"
                  />
                  <button
                    type="button"
                    onClick={handleAddSource}
                    className="btn-secondary"
                    data-testid="add-source-button"
                  >
                    Добавить
                  </button>
                </div>
                
                {formData.sources.length > 0 && (
                  <div className="space-y-2">
                    {formData.sources.map((source, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                        <span className="text-sm text-gray-700 flex-1 break-all">{source}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveSource(index)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          data-testid={`remove-source-${index}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="form-group">
                <label className="form-label" htmlFor="description">
                  Описание проекта
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Краткое описание проекта..."
                  rows={3}
                />
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label" htmlFor="notes">
                  Заметки
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Дополнительная информация о проекте..."
                  rows={4}
                  data-testid="notes-textarea"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary"
                  disabled={loading || isCheckingDuplicates}
                  data-testid="cancel-button"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || isCheckingDuplicates}
                  data-testid="create-project-submit"
                >
                  {isCheckingDuplicates ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Проверка дублей...
                    </div>
                  ) : loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Создание...
                    </div>
                  ) : (
                    'Создать проект'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Duplicate Warning Modal */}
      <DuplicateWarning
        duplicates={duplicates}
        onConfirm={handleConfirmDuplicateCreation}
        onCancel={handleCancelDuplicateCreation}
        isVisible={showDuplicateWarning}
      />
    </>
  );
};

export default CreateProjectModal;